import { ArrowRight, Check } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import CompanyLogoMarquee from "@/components/CompanyLogoMarquee";
import { AI_LEADERS_PROGRAM } from "@/data/aiLeadersProgram";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { buildOrganizationJourneyUrl } from "@/utils/organizationJourney";
import { trackClick } from "@/utils/posthog";

const AIProgramSelector = () => {
  const search = useHydratedSearch();
  const offerHref = buildOrganizationJourneyUrl("/ai-30", search, {
    source: "ai", location: "team_program", audience: "organization",
  });
  const callHref = buildOrganizationJourneyUrl("/ai-call", search, {
    source: "ai", location: "team_scope", audience: "organization",
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="AI 30 for Teams | Practical AI Training | Tech Leaders"
        description="Bring your team to AI 30: 30 days of practical AI training, workflow building, and adoption support. Smaller cohorts are welcome."
        keywords={["AI 30", "team AI training", "AI leadership", "AI adoption"]}
      />
      <Navigation />
      <main>
        <section className="mx-auto max-w-6xl px-4 pb-16 pt-28 sm:px-6 sm:pb-20 sm:pt-36 lg:px-8">
          <p className="text-sm font-semibold text-blue-300">AI 30 · For workplace teams</p>
          <h1 className="mt-5 max-w-4xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">Build a team that puts AI to work.</h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">Bring your people and the work they want to improve. In 30 days, we help your team build useful AI workflows, check the results, measure what changes, and bring colleagues with them.</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href={offerHref} className="inline-flex min-h-12 items-center gap-3 rounded-md bg-primary px-6 py-3 font-semibold text-primary-foreground hover:bg-primary/90" onClick={() => trackClick("AI Programs - Explore AI 30", { destination: offerHref, audience: "organization" })}>Explore AI 30 for your team <ArrowRight size={18} aria-hidden="true" /></a>
            <a href={callHref} className="inline-flex min-h-12 items-center gap-2 font-semibold text-blue-300 underline underline-offset-4">Discuss your cohort <ArrowRight size={18} aria-hidden="true" /></a>
          </div>
          <p className="mt-5 text-sm text-muted-foreground">Seats for your organization. Smaller cohorts welcome. Scope and schedule agreed in your proposal.</p>
          <ul className="mt-12 grid gap-5 border-y border-border py-8 sm:grid-cols-3">
            {[
              `${AI_LEADERS_PROGRAM.liveHours} live training hours, on-site or virtual`,
              `${AI_LEADERS_PROGRAM.supportDays} days of implementation support`,
              "Practical AI skills and the leadership to help people use them",
            ].map((item) => <li key={item} className="flex items-start gap-3 text-base"><Check size={20} aria-hidden="true" className="mt-0.5 shrink-0 text-blue-300" />{item}</li>)}
          </ul>
        </section>
        <CompanyLogoMarquee heading="Training leaders and staff at companies like" />
      </main>
      <Footer />
    </div>
  );
};

export default AIProgramSelector;
